import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Copy, Download, KeyRound } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { homePath, useMe, useSetMe } from '@/lib/auth';
import type { Me } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/field';
import { Alert, Spinner } from '@/components/ui/misc';
import { AuthLayout } from './AuthLayout';
import { safeNext } from './SignIn';

async function signOut(setMe: (m: Me | null) => void) {
  await api.post('/api/auth/logout').catch(() => {});
  setMe(null);
}

/** Second sign-in step: a code from the authenticator app, or a recovery code. */
export function MfaVerify() {
  const { data: me, isLoading } = useMe();
  const setMe = useSetMe();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (isLoading) return <Spinner className="h-full" />;
  if (!me) return <Navigate to="/signin" replace />;
  if (me.mfa === 'ok') return <Navigate to={homePath(me)} replace />;
  if (me.mfa === 'enroll') return <Navigate to="/mfa/setup" replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<Me>('/api/auth/mfa/verify', { code: code.trim() });
      setMe(res);
      nav(safeNext(params.get('next')) ?? homePath(res), { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Two-factor authentication"
      subtitle={
        useRecovery
          ? 'Enter one of your saved recovery codes. Each works once.'
          : 'Enter the 6-digit code from your authenticator app.'
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        {error && <Alert tone="bad">{error}</Alert>}
        <Field label={useRecovery ? 'Recovery code' : 'Authentication code'} htmlFor="code">
          <Input
            id="code"
            autoFocus
            required
            autoComplete="one-time-code"
            inputMode={useRecovery ? 'text' : 'numeric'}
            placeholder={useRecovery ? 'xxxxx-xxxxx' : '123456'}
            className="tabular text-center text-lg tracking-[0.3em]"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </Field>
        <Button type="submit" size="lg" loading={busy}>
          Verify
        </Button>
        <div className="flex justify-between text-[13px]">
          <button
            type="button"
            className="text-brand-text hover:underline"
            onClick={() => (setUseRecovery(!useRecovery), setCode(''), setError(null))}
          >
            {useRecovery ? 'Use authenticator code' : 'Use a recovery code'}
          </button>
          <button type="button" className="text-muted hover:text-text" onClick={() => signOut(setMe)}>
            Sign out
          </button>
        </div>
      </form>
    </AuthLayout>
  );
}

interface Enrollment {
  otpauthUri: string;
  secret: string;
  qrSvg: string;
}

/** Set up TOTP: scan, confirm a first code, save recovery codes. */
export function MfaSetup() {
  const { data: me, isLoading } = useMe();
  const setMe = useSetMe();
  const nav = useNavigate();
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const canEnroll = me && !me.user.mfaEnabled && me.mfa !== 'verify';
  useEffect(() => {
    if (canEnroll && !enrollment && !recovery) {
      api.post<Enrollment>('/api/auth/mfa/enroll/start').then(setEnrollment, (e) => setError(errorMessage(e)));
    }
  }, [canEnroll, enrollment, recovery]);

  if (isLoading) return <Spinner className="h-full" />;
  if (!me) return <Navigate to="/signin" replace />;
  if (me.mfa === 'verify') return <Navigate to="/mfa" replace />;
  if (me.user.mfaEnabled && !recovery) return <Navigate to={homePath(me)} replace />;

  async function confirm(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ recoveryCodes: string[] }>('/api/auth/mfa/enroll/confirm', { code: code.trim() });
      setRecovery(res.recoveryCodes);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function finish() {
    const fresh = await api.get<Me>('/api/auth/me');
    setMe(fresh);
    nav(homePath(fresh), { replace: true });
  }

  if (recovery) {
    const text = `HELM recovery codes for ${me.user.email}\nEach code works once.\n\n${recovery.join('\n')}\n`;
    const download = () => {
      const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
      const a = Object.assign(document.createElement('a'), { href: url, download: 'helm-recovery-codes.txt' });
      a.click();
      URL.revokeObjectURL(url);
    };
    return (
      <AuthLayout
        title="Save your recovery codes"
        subtitle="If you lose your phone, each of these signs you in once. They will not be shown again."
        wide
      >
        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-md border border-border bg-surface-2 px-5 py-4 font-mono text-sm tabular">
          {recovery.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => navigator.clipboard.writeText(text)}>
            <Copy /> Copy
          </Button>
          <Button variant="secondary" size="sm" onClick={download}>
            <Download /> Download
          </Button>
        </div>
        <label className="mt-5 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={saved}
            onChange={(e) => setSaved(e.target.checked)}
            className="size-4 accent-[var(--brand)]"
          />
          I have stored these codes somewhere safe
        </label>
        <Button size="lg" className="mt-4 w-full" disabled={!saved} onClick={finish}>
          Continue to HELM
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Set up two-factor authentication"
      subtitle={
        me.mfa === 'enroll'
          ? 'Your role requires a second factor before you can continue.'
          : 'Add a second factor to your account.'
      }
      wide
    >
      {error && (
        <Alert tone="bad" className="mb-4">
          {error}
        </Alert>
      )}
      {!enrollment ? (
        <Spinner />
      ) : (
        <form onSubmit={confirm} className="flex flex-col gap-4">
          <ol className="flex flex-col gap-1 text-sm text-muted">
            <li>1. Open an authenticator app (Microsoft Authenticator, Google Authenticator, 1Password).</li>
            <li>2. Scan this code, or enter the key by hand.</li>
            <li>3. Type the 6-digit code it shows.</li>
          </ol>
          <div className="flex items-center gap-5">
            <img
              src={`data:image/svg+xml;utf8,${encodeURIComponent(enrollment.qrSvg)}`}
              alt="QR code for your authenticator app"
              className="size-40 rounded-md border border-border bg-white p-1.5"
            />
            <div className="min-w-0 text-xs text-muted">
              <p className="mb-1 flex items-center gap-1 font-medium text-text">
                <KeyRound className="size-3.5" /> Setup key
              </p>
              <code className="block break-all rounded bg-surface-2 px-2 py-1.5 font-mono text-[12px] text-text">
                {enrollment.secret.match(/.{1,4}/g)?.join(' ')}
              </code>
            </div>
          </div>
          <Field label="Authentication code" htmlFor="code">
            <Input
              id="code"
              autoFocus
              required
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              className="tabular text-center text-lg tracking-[0.3em]"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
          <Button type="submit" size="lg" loading={busy}>
            Turn on two-factor authentication
          </Button>
          <button type="button" className="text-[13px] text-muted hover:text-text" onClick={() => signOut(setMe)}>
            Sign out
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
